import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { PODPhotoManager } from '../components/PODPhotoManager';
import ItemInspectionSection from '../components/ItemInspectionSection';
import ItemDriverDetailsSection from '../components/ItemDriverDetailsSection';
import { VehicleInspectionChecklist } from '../components/VehicleInspectionChecklist';
import { 
  Plus, Search, FileText, Eye, RefreshCw, Download, Truck, Check,
  Package, Users, AlertTriangle, CheckCircle, Clock, Send, ChevronsUpDown, AlertCircle, Camera, MapPin
} from 'lucide-react';
import {
  getCompanies, getClientPOs, canCreateDelivery,
  createDeliveryNoteV2, getDeliveryNotesV2, getDeliveryNoteV2,
  markDNDelivered, acknowledgeDNDelivery, getDeliveryNotePDF, getSignedDeliveryPDF, getDNStats,
  getAssets, markDNPOReceived, getMyScopedClientPOs, listDrivers
} from '../lib/api';
import { useAuth } from '../context/AuthContext';

const statusColors = {
  draft: 'bg-gray-100 text-gray-800',
  issued: 'bg-blue-100 text-blue-800',
  delivered: 'bg-green-100 text-green-800',
  acknowledged: 'bg-purple-100 text-purple-800'
};

const typeIcons = {
  equipment: Package,
  manpower: Users,
  material: FileText,
  transportation: Truck
};

export const DeliveryNotesV2Page = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSalesOnly = user?.role === 'sales';
  const [companies, setCompanies] = useState([]);
  const [clientPOs, setClientPOs] = useState([]);
  const [allClientPOs, setAllClientPOs] = useState([]); // Include POs without documents for Risk Mode
  const [assets, setAssets] = useState([]);
  const [deliveryNotes, setDeliveryNotes] = useState([]);
  const [savedDrivers, setSavedDrivers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  
  // Dialog states
  const [createDNOpen, setCreateDNOpen] = useState(false);
  const [viewDNOpen, setViewDNOpen] = useState(false);
  const [selectedDN, setSelectedDN] = useState(null);
  const [inspComplete, setInspComplete] = useState({}); // {item_id: boolean}
  const [confirmDeliverOpen, setConfirmDeliverOpen] = useState(false);

  // Stable callbacks for children — new inline lambdas inside .map would
  // create a fresh function every render and fight react-hooks stability
  // rules. Guarded state updates prevent infinite render loops.
  const handleInspValidityChange = useCallback((itemId, ok) => {
    setInspComplete((p) => (p[itemId] === ok ? p : { ...p, [itemId]: ok }));
  }, []);
  const handleInspectionChange = useCallback((itemId, next) => {
    setSelectedDN((prev) => {
      if (!prev) return prev;
      const items = (prev.items || []).map((x) =>
        (x.item_id || '') === (itemId || '')
          ? { ...x, inspection: next }
          : x
      );
      return { ...prev, items };
    });
  }, []);
  const [selectedPOForDN, setSelectedPOForDN] = useState(null);
  
  // Searchable PO dropdown state
  const [poSearchOpen, setPoSearchOpen] = useState(false);
  const [poSearchQuery, setPoSearchQuery] = useState('');
  
  // Risk Mode state
  const [riskModeEnabled, setRiskModeEnabled] = useState(false);
  
  // Form state - REMOVED rate and amount fields from items
  const [dnForm, setDnForm] = useState({
    client_po_id: '',
    dn_type: 'equipment',
    delivery_date: new Date().toISOString().split('T')[0],
    // Ordering client (HO / billing) contact
    ordering_client_contact_name: '',
    ordering_client_contact_phone: '',
    // Site delivery location (physical drop-off)
    delivery_location: '',
    site_supervisor_name: '',
    site_supervisor_mobile: '',
    site_maps_url: '',
    site_gps_manual: null,
    items: [{ description: '', quantity: 1, unit: 'unit', include_driver: false }],
    risk_mode: false,
    company_id: '',
    client_name: '',
    client_followup_email: '',
    sales_person_email: '',
    notes: '',
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      // Sales users see only their OWN client POs within their assigned
      // company_ids. Managers / Admin / Ops / Finance see everything.
      const posFetch = isSalesOnly
        ? getMyScopedClientPOs(null, 'active', true)
        : getClientPOs({ status: 'active', has_document: true });
      const allPosFetch = isSalesOnly
        ? getMyScopedClientPOs(null, 'active')
        : getClientPOs({ status: 'active' });
      const [companiesRes, posRes, allPosRes, assetsRes, driversRes] = await Promise.all([
        getCompanies(),
        posFetch,
        allPosFetch,
        // Only vehicles that are actually available for a new dispatch —
        // backend auto-scopes to company_ids for pure sales users.
        getAssets(null, 'available'),
        listDrivers().catch(() => ({ data: [] }))
      ]);
      setCompanies(companiesRes.data);
      setClientPOs(posRes.data);
      setAllClientPOs(allPosRes.data);
      setAssets(assetsRes.data || []);
      setSavedDrivers(driversRes.data || []);
      
      // Fetch DNs
      const filters = {};
      if (selectedCompany) filters.company_id = selectedCompany;
      if (statusFilter) filters.status = statusFilter;
      if (typeFilter) filters.dn_type = typeFilter;
      
      const [dnsRes, statsRes] = await Promise.all([
        getDeliveryNotesV2(filters),
        getDNStats(selectedCompany)
      ]);
      setDeliveryNotes(dnsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [selectedCompany, statusFilter, typeFilter, isSalesOnly]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSelectPO = async (poId) => {
    try {
      // Find the PO from the appropriate list
      const availablePOs = riskModeEnabled ? allClientPOs : clientPOs;
      const po = availablePOs.find(p => p.id === poId);
      
      if (!po) {
        toast.error('PO not found');
        return;
      }
      
      // Skip validation for risk mode POs without documents
      if (!riskModeEnabled || po.document_uploaded) {
        const res = await canCreateDelivery(poId);
        if (!res.data.can_create) {
          toast.error(res.data.reason);
          return;
        }
      }
      
      // Check if PO is missing document (Risk Mode)
      const isRiskMode = !po.document_uploaded;
      
      setSelectedPOForDN(po);
      setDnForm({
        ...dnForm,
        client_po_id: poId,
        dn_type: po.po_type,
        risk_mode: isRiskMode
      });
      setPoSearchOpen(false);
    } catch (error) {
      toast.error('Failed to validate PO');
    }
  };

  const handleAddItem = () => {
    setDnForm({
      ...dnForm,
      items: [...dnForm.items, { description: '', quantity: 1, unit: 'unit', include_driver: false }]
    });
  };

  // Merge partial driver-field updates onto a specific item
  const handleItemDriverChange = (index, patch) => {
    const newItems = [...dnForm.items];
    newItems[index] = { ...newItems[index], ...patch };
    setDnForm({ ...dnForm, items: newItems });
  };

  const handleToggleIncludeDriver = (index, checked) => {
    const newItems = [...dnForm.items];
    if (checked) {
      newItems[index] = { ...newItems[index], include_driver: true };
    } else {
      // Wipe driver payload if operator is no longer required
      const {
        driver_name, driver_mobile, driver_iqama_number, driver_license_number,
        driver_iqama_url, driver_license_url, driver_iqama_filename, driver_license_filename,
        ...rest
      } = newItems[index];
      newItems[index] = { ...rest, include_driver: false };
    }
    setDnForm({ ...dnForm, items: newItems });
  };

  const handleRemoveItem = (index) => {
    const newItems = dnForm.items.filter((_, i) => i !== index);
    setDnForm({ ...dnForm, items: newItems });
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...dnForm.items];
    newItems[index][field] = value;
    setDnForm({ ...dnForm, items: newItems });
  };

  // Auto-fill item details from own fleet asset (vehicle no, chassis no, year, model)
  const handleSelectAsset = (index, assetId) => {
    const asset = assets.find(a => a.id === assetId);
    if (!asset) return;
    const descParts = [
      `${asset.asset_type} - ${asset.make} ${asset.model} (${asset.year})`,
      `Reg No: ${asset.registration_number}`
    ];
    if (asset.chassis_number) descParts.push(`Chassis No: ${asset.chassis_number}`);
    const newItems = [...dnForm.items];
    newItems[index] = {
      ...newItems[index],
      asset_id: asset.id,
      description: descParts.join(' | '),
      registration_number: asset.registration_number,
      chassis_number: asset.chassis_number || null,
      make: asset.make,
      model: asset.model,
      year: asset.year
    };
    setDnForm({ ...dnForm, items: newItems });
  };

  const handleMarkPOReceived = async (dn) => {
    const poNumber = window.prompt('Enter the received Client PO number:');
    if (!poNumber) return;
    try {
      await markDNPOReceived(dn.id, { po_number: poNumber });
      toast.success('PO marked as received — daily follow-ups stopped');
      setViewDNOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark PO as received');
    }
  };

  const handleCreateDN = async () => {
    try {
      if (!dnForm.client_po_id && !riskModeEnabled) {
        toast.error('Please select a Client PO (or enable Risk Mode to create without one)');
        return;
      }
      if (riskModeEnabled && !dnForm.client_po_id) {
        if (!dnForm.company_id || !dnForm.client_name) {
          toast.error('Company and Client Name are required in Risk Mode without a PO');
          return;
        }
        if (!dnForm.client_followup_email) {
          toast.error('Client follow-up email is required in Risk Mode (used to chase the PO daily)');
          return;
        }
      }
      
      const payload = {
        ...dnForm,
        risk_mode: riskModeEnabled || dnForm.risk_mode,
        company_name: companies.find(c => c.id === dnForm.company_id)?.name
      };
      await createDeliveryNoteV2(payload);
      
      if (payload.risk_mode) {
        toast.warning('Delivery Note created in RISK MODE — daily PO follow-up emails will be sent until PO is received!');
      } else {
        toast.success('Delivery Note created successfully');
      }
      
      setCreateDNOpen(false);
      setSelectedPOForDN(null);
      setDnForm({
        client_po_id: '',
        dn_type: 'equipment',
        delivery_date: new Date().toISOString().split('T')[0],
        ordering_client_contact_name: '',
        ordering_client_contact_phone: '',
        delivery_location: '',
        site_supervisor_name: '',
        site_supervisor_mobile: '',
        site_maps_url: '',
        site_gps_manual: null,
        items: [{ description: '', quantity: 1, unit: 'unit', include_driver: false }],
        risk_mode: false,
        company_id: '',
        client_name: '',
        client_followup_email: '',
        sales_person_email: '',
        notes: ''
      });
      setRiskModeEnabled(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create Delivery Note');
    }
  };

  const handleMarkDelivered = async (dnId) => {
    try {
      const res = await markDNDelivered(dnId, {});
      toast.success('Marked as delivered');
      
      if (res.data.timesheet_reminder_created) {
        toast.info('Timesheet reminder created automatically');
      }
      if (res.data.invoice_pending) {
        toast.info('Added to invoice pending queue');
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as delivered');
    }
  };

  const handleAcknowledge = async (dnId) => {
    const name = window.prompt('Enter name of person acknowledging delivery:');
    if (!name) return;
    
    try {
      await acknowledgeDNDelivery(dnId, { acknowledged_by: name });
      toast.success('Delivery acknowledged');
      fetchData();
    } catch (error) {
      toast.error('Failed to record acknowledgment');
    }
  };

  const handleDownloadPDF = async (dn) => {
    try {
      const res = await getDeliveryNotePDF(dn.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `DN_${dn.dn_number}.pdf`;
      a.click();
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleDownloadSignedPDF = async (dn) => {
    try {
      const res = await getSignedDeliveryPDF(dn.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `DN_${dn.dn_number}_signed.pdf`;
      a.click();
      toast.success('Signed Delivery PDF downloaded');
    } catch (error) {
      // Blob-response error → parse JSON detail
      let msg = 'Failed to download signed PDF';
      try {
        const text = await error.response?.data?.text?.();
        if (text) msg = JSON.parse(text).detail || msg;
      } catch { /* ignore */ }
      toast.error(msg);
    }
  };

  const filteredDNs = deliveryNotes.filter(dn => {
    if (searchQuery) {
      const search = searchQuery.toLowerCase();
      return (
        dn.dn_number?.toLowerCase().includes(search) ||
        dn.client_name?.toLowerCase().includes(search) ||
        dn.client_po_number?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  return (
    <div className="space-y-6" data-testid="delivery-notes-v2-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight">Delivery Notes</h1>
          <p className="text-muted-foreground">Track equipment, manpower, material deliveries</p>
        </div>
        <Button onClick={() => { setCreateDNOpen(true); fetchData(); }} data-testid="create-dn-btn">
          <Plus className="h-4 w-4 mr-2" />
          Create Delivery Note
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold">{stats.total}</div>
              <p className="text-xs text-muted-foreground">Total DNs</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-gray-600">{stats.by_status?.draft || 0}</div>
              <p className="text-xs text-muted-foreground">Draft</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-blue-600">{stats.by_status?.issued || 0}</div>
              <p className="text-xs text-muted-foreground">Issued</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-green-600">{stats.by_status?.delivered || 0}</div>
              <p className="text-xs text-muted-foreground">Delivered</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-purple-600">{stats.by_status?.acknowledged || 0}</div>
              <p className="text-xs text-muted-foreground">Acknowledged</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-orange-600">{stats.invoice_pending || 0}</div>
              <p className="text-xs text-muted-foreground">Pending Invoice</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search DN#, client..."
                className="pl-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-input"
              />
            </div>
            <Select value={selectedCompany || "all"} onValueChange={(v) => setSelectedCompany(v === "all" ? "" : v)}>
              <SelectTrigger>
                <SelectValue placeholder="All Companies" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Companies</SelectItem>
                {companies.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
              <SelectTrigger>
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="issued">Issued</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
                <SelectItem value="acknowledged">Acknowledged</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter || "all"} onValueChange={(v) => setTypeFilter(v === "all" ? "" : v)}>
              <SelectTrigger>
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="equipment">Equipment</SelectItem>
                <SelectItem value="manpower">Manpower</SelectItem>
                <SelectItem value="material">Material</SelectItem>
                <SelectItem value="transportation">Transportation</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* DN List */}
      <Card>
        <CardHeader>
          <CardTitle>Delivery Notes ({filteredDNs.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : filteredDNs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No delivery notes found. Create a Client PO first, then create delivery notes.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-3 font-medium">DN Number</th>
                    <th className="pb-3 font-medium">Client PO</th>
                    <th className="pb-3 font-medium">Client</th>
                    <th className="pb-3 font-medium">Type</th>
                    <th className="pb-3 font-medium">Date</th>
                    <th className="pb-3 font-medium">Status</th>
                    <th className="pb-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDNs.map(dn => {
                    const TypeIcon = typeIcons[dn.dn_type] || FileText;
                    const isRiskMode = dn.risk_mode || false;
                    return (
                      <tr key={dn.id} className={`border-b hover:bg-muted/50 ${isRiskMode ? 'bg-red-50' : ''}`} data-testid={`dn-row-${dn.id}`}>
                        <td className="py-3 font-medium">
                          <span className="flex items-center gap-2">
                            {dn.dn_number}
                            {isRiskMode && (
                              <Badge className="bg-red-100 text-red-800 text-xs" data-testid={`risk-badge-${dn.id}`}>
                                <AlertCircle className="h-3 w-3 mr-1" />
                                RISK
                              </Badge>
                            )}
                          </span>
                        </td>
                        <td className="py-3">{dn.client_po_number}</td>
                        <td className="py-3">{dn.client_name}</td>
                        <td className="py-3">
                          <div className="flex items-center gap-2">
                            <TypeIcon className="h-4 w-4" />
                            <span className="capitalize">{dn.dn_type}</span>
                          </div>
                        </td>
                        <td className="py-3">{dn.delivery_date}</td>
                        <td className="py-3">
                          <Badge className={statusColors[dn.status]}>
                            {dn.status}
                          </Badge>
                        </td>
                        <td className="py-3">
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setSelectedDN(dn);
                                setViewDNOpen(true);
                              }}
                              data-testid={`view-dn-${dn.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDownloadPDF(dn)}
                              data-testid={`download-dn-${dn.id}`}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            {(dn.status === 'delivered' || dn.status === 'acknowledged') && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-emerald-700"
                                onClick={() => handleDownloadSignedPDF(dn)}
                                title="Download Signed Delivery PDF (photos + signature)"
                                data-testid={`signed-pdf-${dn.id}`}
                              >
                                <FileText className="h-4 w-4" />
                              </Button>
                            )}
                            {dn.status !== 'acknowledged' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-blue-600"
                                onClick={() => navigate(`/mobile/delivery/${dn.id}`)}
                                title="Upload delivery photos & signature"
                                data-testid={`upload-pod-${dn.id}`}
                              >
                                <Camera className="h-4 w-4" />
                              </Button>
                            )}
                            {dn.status === 'draft' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-green-600"
                                onClick={() => handleMarkDelivered(dn.id)}
                                data-testid={`deliver-dn-${dn.id}`}
                              >
                                <Send className="h-4 w-4" />
                              </Button>
                            )}
                            {dn.status === 'delivered' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-purple-600"
                                onClick={() => handleAcknowledge(dn.id)}
                                data-testid={`acknowledge-dn-${dn.id}`}
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create DN Dialog */}
      <Dialog open={createDNOpen} onOpenChange={setCreateDNOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Delivery Note</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {/* Risk Mode Toggle */}
            <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <input
                type="checkbox"
                id="risk-mode"
                checked={riskModeEnabled}
                onChange={(e) => {
                  setRiskModeEnabled(e.target.checked);
                  setSelectedPOForDN(null);
                  setDnForm({...dnForm, client_po_id: '', risk_mode: e.target.checked});
                }}
                className="h-4 w-4"
                data-testid="risk-mode-toggle"
              />
              <Label htmlFor="risk-mode" className="flex items-center gap-2 cursor-pointer">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <span className="font-medium text-amber-800">Risk Mode</span>
                <span className="text-xs text-amber-600">- Create DN without a Client PO (daily follow-up emails until PO received)</span>
              </Label>
            </div>

            {/* Searchable Client PO Dropdown */}
            <div>
              <Label>Select Client PO {riskModeEnabled ? '(optional in Risk Mode)' : '*'}</Label>
              <Popover open={poSearchOpen} onOpenChange={setPoSearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={poSearchOpen}
                    className="w-full justify-between"
                    data-testid="select-po-combobox"
                  >
                    {dnForm.client_po_id
                      ? (() => {
                          const availablePOs = riskModeEnabled ? allClientPOs : clientPOs;
                          const selected = availablePOs.find(po => po.id === dnForm.client_po_id);
                          return selected ? `${selected.po_number} - ${selected.client_name}` : 'Select a Client PO...';
                        })()
                      : 'Search and select a Client PO...'}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[500px] p-0" align="start">
                  <Command>
                    <CommandInput 
                      placeholder="Search PO number or client name..." 
                      value={poSearchQuery}
                      onValueChange={setPoSearchQuery}
                      data-testid="po-search-input"
                    />
                    <CommandList>
                      <CommandEmpty>No Client PO found.</CommandEmpty>
                      <CommandGroup>
                        {(riskModeEnabled ? allClientPOs : clientPOs.filter(po => po.document_uploaded))
                          .filter(po => {
                            if (!poSearchQuery) return true;
                            const search = poSearchQuery.toLowerCase();
                            return (
                              po.po_number?.toLowerCase().includes(search) ||
                              po.client_name?.toLowerCase().includes(search)
                            );
                          })
                          .slice(0, 50)
                          .map(po => (
                            <CommandItem
                              key={po.id}
                              value={po.id}
                              onSelect={() => handleSelectPO(po.id)}
                              className="flex justify-between items-center"
                              data-testid={`po-option-${po.id}`}
                            >
                              <div className="flex flex-col">
                                <span className="font-medium">{po.po_number}</span>
                                <span className="text-xs text-muted-foreground">{po.client_name}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="capitalize text-xs">{po.po_type}</Badge>
                                {!po.document_uploaded && (
                                  <Badge className="bg-red-100 text-red-800 text-xs">No Doc</Badge>
                                )}
                              </div>
                            </CommandItem>
                          ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              
              {!riskModeEnabled && clientPOs.filter(po => po.document_uploaded).length === 0 && (
                <p className="text-sm text-orange-600 mt-1">
                  <AlertTriangle className="h-4 w-4 inline mr-1" />
                  No active Client POs with uploaded documents. Enable Risk Mode or upload a PO document first.
                </p>
              )}
            </div>

            {/* Risk Mode Details */}
            {riskModeEnabled && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg space-y-3">
                <p className="text-sm font-medium text-red-700">
                  Risk Mode Details {!selectedPOForDN && '(no PO selected — enter client details manually)'}
                </p>
                {!selectedPOForDN && (
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label>Company *</Label>
                      <Select value={dnForm.company_id} onValueChange={(v) => setDnForm({...dnForm, company_id: v})}>
                        <SelectTrigger data-testid="risk-company-select">
                          <SelectValue placeholder="Select company" />
                        </SelectTrigger>
                        <SelectContent>
                          {companies.map(c => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Client Name *</Label>
                      <Input
                        value={dnForm.client_name}
                        onChange={(e) => setDnForm({...dnForm, client_name: e.target.value})}
                        placeholder="Client name"
                        data-testid="risk-client-name-input"
                      />
                    </div>
                    <div>
                      <Label>DN Type *</Label>
                      <Select value={dnForm.dn_type} onValueChange={(v) => setDnForm({...dnForm, dn_type: v})}>
                        <SelectTrigger data-testid="risk-dn-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="equipment">Equipment</SelectItem>
                          <SelectItem value="manpower">Manpower</SelectItem>
                          <SelectItem value="material">Material</SelectItem>
                          <SelectItem value="transportation">Transportation</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Client Follow-up Email *</Label>
                    <Input
                      type="email"
                      value={dnForm.client_followup_email}
                      onChange={(e) => setDnForm({...dnForm, client_followup_email: e.target.value})}
                      placeholder="client@company.com"
                      data-testid="risk-client-email-input"
                    />
                    <p className="text-xs text-red-500 mt-1">Daily PO follow-up emails will be sent here until PO is received</p>
                  </div>
                  <div>
                    <Label>Sales Person Email</Label>
                    <Input
                      type="email"
                      value={dnForm.sales_person_email}
                      onChange={(e) => setDnForm({...dnForm, sales_person_email: e.target.value})}
                      placeholder="sales@ourcompany.com"
                      data-testid="risk-sales-email-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Will be CC'd on every follow-up</p>
                  </div>
                </div>
              </div>
            )}

            {/* Risk Mode Warning */}
            {dnForm.risk_mode && selectedPOForDN && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2 text-red-700">
                  <AlertCircle className="h-5 w-5" />
                  <span className="font-medium">RISK MODE ACTIVE</span>
                </div>
                <p className="text-sm text-red-600 mt-1">
                  This PO ({selectedPOForDN.po_number}) does not have an uploaded document. 
                  Creating a DN will flag both the DN and PO as RED until the document is uploaded.
                </p>
              </div>
            )}

            {(selectedPOForDN || riskModeEnabled) && (
              <>
                {selectedPOForDN && (
                <Card className="bg-muted/50">
                  <CardContent className="pt-4">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Client:</span>
                        <p className="font-medium">{selectedPOForDN.client_name}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Company:</span>
                        <p className="font-medium">{selectedPOForDN.company_name}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Type:</span>
                        <p className="font-medium capitalize">{selectedPOForDN.po_type}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                )}

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Delivery Date</Label>
                    <Input
                      type="date"
                      value={dnForm.delivery_date}
                      onChange={(e) => setDnForm({...dnForm, delivery_date: e.target.value})}
                      data-testid="delivery-date-input"
                    />
                  </div>
                </div>

                {/* --- Ordering Client (Head Office / Billing) --- */}
                <Card className="bg-slate-50/50 border-slate-200" data-testid="ordering-client-section">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="h-4 w-4 text-slate-600" />
                      <Label className="font-semibold text-slate-800">Ordering Client (Head Office / Billing)</Label>
                    </div>
                    <p className="text-xs text-muted-foreground mb-3">
                      Who placed the order. Used for invoicing and PO follow-ups.
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">HO Contact Name</Label>
                        <Input
                          value={dnForm.ordering_client_contact_name}
                          onChange={(e) => setDnForm({...dnForm, ordering_client_contact_name: e.target.value})}
                          placeholder="Person who signed the PO"
                          data-testid="ordering-contact-name-input"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">HO Contact Mobile</Label>
                        <Input
                          type="tel"
                          value={dnForm.ordering_client_contact_phone}
                          onChange={(e) => setDnForm({...dnForm, ordering_client_contact_phone: e.target.value})}
                          placeholder="+9665..."
                          data-testid="ordering-contact-phone-input"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* --- Site Delivery Location --- */}
                <Card className="bg-emerald-50/40 border-emerald-200" data-testid="site-delivery-section">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 mb-2">
                      <MapPin className="h-4 w-4 text-emerald-700" />
                      <Label className="font-semibold text-emerald-900">Site Delivery Location</Label>
                      <span className="text-[10px] text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5">
                        Where the driver drops off
                      </span>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs">Site Address *</Label>
                        <Input
                          value={dnForm.delivery_location}
                          onChange={(e) => setDnForm({...dnForm, delivery_location: e.target.value})}
                          placeholder="Full site address (e.g. Riyadh, Al Malaz, Bldg 12)"
                          data-testid="site-address-input"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Site Supervisor / Receiving Contact Name *</Label>
                          <Input
                            value={dnForm.site_supervisor_name}
                            onChange={(e) => setDnForm({...dnForm, site_supervisor_name: e.target.value})}
                            placeholder="Different from PO signatory if applicable"
                            data-testid="site-supervisor-name-input"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Site Supervisor Mobile *</Label>
                          <Input
                            type="tel"
                            value={dnForm.site_supervisor_mobile}
                            onChange={(e) => setDnForm({...dnForm, site_supervisor_mobile: e.target.value})}
                            placeholder="+9665..."
                            data-testid="site-supervisor-mobile-input"
                          />
                          <p className="text-[10px] text-muted-foreground mt-1">
                            Receives a WhatsApp on mobilization & delivery completion.
                          </p>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Google Maps Link</Label>
                        <div className="flex gap-2">
                          <Input
                            value={dnForm.site_maps_url}
                            onChange={(e) => setDnForm({...dnForm, site_maps_url: e.target.value})}
                            placeholder="Paste Google Maps URL or use Capture GPS →"
                            data-testid="site-maps-url-input"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="shrink-0"
                            data-testid="capture-site-gps-btn"
                            onClick={() => {
                              if (!navigator.geolocation) {
                                toast.error('Geolocation not supported on this device');
                                return;
                              }
                              toast.info('Capturing GPS…');
                              navigator.geolocation.getCurrentPosition(
                                (pos) => {
                                  const { latitude, longitude, accuracy } = pos.coords;
                                  const url = `https://maps.google.com/?q=${latitude},${longitude}`;
                                  setDnForm((prev) => ({
                                    ...prev,
                                    site_gps_manual: { latitude, longitude, accuracy_m: accuracy },
                                    site_maps_url: prev.site_maps_url || url,
                                  }));
                                  toast.success(`GPS captured (±${Math.round(accuracy)} m)`);
                                },
                                (err) => toast.error(`GPS failed: ${err.message}`),
                                { enableHighAccuracy: true, timeout: 10000 }
                              );
                            }}
                          >
                            <MapPin className="h-4 w-4 mr-1" />
                            Capture GPS
                          </Button>
                        </div>
                        {dnForm.site_gps_manual && (
                          <p className="text-xs text-emerald-700 mt-1">
                            Pinned: {Number(dnForm.site_gps_manual.latitude).toFixed(5)},{' '}
                            {Number(dnForm.site_gps_manual.longitude).toFixed(5)}
                            {dnForm.site_gps_manual.accuracy_m != null &&
                              ` (±${Math.round(dnForm.site_gps_manual.accuracy_m)} m)`}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Items - per-item Driver/Operator toggle rendered inline below each item */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <Label>Items</Label>
                    <Button size="sm" variant="outline" onClick={handleAddItem}>
                      <Plus className="h-4 w-4 mr-1" /> Add Item
                    </Button>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="grid grid-cols-12 gap-2 text-xs text-muted-foreground font-medium">
                      <div className="col-span-7">Description</div>
                      <div className="col-span-2">Quantity</div>
                      <div className="col-span-2">Unit</div>
                      <div className="col-span-1"></div>
                    </div>
                    {dnForm.items.map((item, idx) => (
                      <div key={idx} className="space-y-1 rounded-md border border-slate-200 p-2 bg-slate-50/40">
                        {['equipment', 'transportation'].includes(dnForm.dn_type) && (
                          <Select value={item.asset_id || ''} onValueChange={(v) => handleSelectAsset(idx, v)}>
                            <SelectTrigger className="h-8 text-xs" data-testid={`item-asset-select-${idx}`}>
                              <SelectValue placeholder="Auto-fill from own fleet (optional)..." />
                            </SelectTrigger>
                            <SelectContent>
                              {assets.map(a => (
                                <SelectItem key={a.id} value={a.id}>
                                  {a.asset_type} - {a.make} {a.model} ({a.year}) | {a.registration_number}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        <div className="grid grid-cols-12 gap-2 items-center">
                        <div className="col-span-7">
                          <Input
                            placeholder="Description (e.g., Excavator 20T)"
                            value={item.description}
                            onChange={(e) => handleItemChange(idx, 'description', e.target.value)}
                            data-testid={`item-desc-${idx}`}
                          />
                        </div>
                        <div className="col-span-2">
                          <Input
                            type="number"
                            placeholder="Qty"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(idx, 'quantity', parseFloat(e.target.value) || 0)}
                            data-testid={`item-qty-${idx}`}
                          />
                        </div>
                        <div className="col-span-2">
                          <Input
                            placeholder="Unit"
                            value={item.unit}
                            onChange={(e) => handleItemChange(idx, 'unit', e.target.value)}
                            data-testid={`item-unit-${idx}`}
                          />
                        </div>
                        <div className="col-span-1">
                          {dnForm.items.length > 1 && (
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-red-600"
                              onClick={() => handleRemoveItem(idx)}
                            >
                              ×
                            </Button>
                          )}
                        </div>
                        </div>

                        {/* Include Driver / Operator toggle (per item) */}
                        <label className="flex items-center gap-2 text-sm pt-1 cursor-pointer">
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            checked={!!item.include_driver}
                            onChange={(e) => handleToggleIncludeDriver(idx, e.target.checked)}
                            data-testid={`item-include-driver-${idx}`}
                          />
                          <span className="font-medium text-slate-700">Include Driver / Operator?</span>
                          {!item.include_driver && (
                            <span className="text-xs text-muted-foreground">— vehicle delivered without operator</span>
                          )}
                        </label>

                        {item.include_driver && (
                          <ItemDriverDetailsSection
                            item={item}
                            index={idx}
                            onChange={(patch) => handleItemDriverChange(idx, patch)}
                            savedDrivers={savedDrivers}
                            onDriverSaved={async () => {
                              try {
                                const r = await listDrivers();
                                setSavedDrivers(r.data || []);
                              } catch { /* ignore */ }
                            }}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Notes field - Price/Totals section removed per P3 */}
                <div>
                  <Label>Notes</Label>
                  <Input
                    value={dnForm.notes}
                    onChange={(e) => setDnForm({...dnForm, notes: e.target.value})}
                    placeholder="Additional notes..."
                    data-testid="notes-input"
                  />
                </div>
              </>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => {
                setCreateDNOpen(false);
                setSelectedPOForDN(null);
                setRiskModeEnabled(false);
              }}>Cancel</Button>
              <Button 
                onClick={handleCreateDN} 
                disabled={!selectedPOForDN && !riskModeEnabled}
                className={(dnForm.risk_mode || riskModeEnabled) ? 'bg-red-600 hover:bg-red-700' : ''}
                data-testid="submit-dn-btn"
              >
                {(dnForm.risk_mode || riskModeEnabled) ? 'Create DN (Risk Mode)' : 'Create Delivery Note'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View DN Dialog */}
      <Dialog open={viewDNOpen} onOpenChange={setViewDNOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Delivery Note Details</DialogTitle>
          </DialogHeader>
          {selectedDN && (
            <div className="space-y-4 pt-4">
              {selectedDN.risk_mode && !selectedDN.po_received && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg space-y-2">
                  <div className="flex items-center gap-2 text-red-700">
                    <AlertCircle className="h-5 w-5" />
                    <span className="font-medium">RISK MODE — Client PO Pending</span>
                  </div>
                  <p className="text-sm text-red-600">
                    Daily follow-up emails are being sent to {selectedDN.client_followup_email || 'the client'}
                    {selectedDN.sales_person_email ? ` and ${selectedDN.sales_person_email}` : ''} until the PO is received.
                  </p>
                  <Button
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => handleMarkPOReceived(selectedDN)}
                    data-testid="mark-po-received-btn"
                  >
                    <Check className="h-4 w-4 mr-1" />
                    Mark PO Received
                  </Button>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">DN Number</Label>
                  <p className="font-medium">{selectedDN.dn_number}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <Badge className={statusColors[selectedDN.status]}>
                    {selectedDN.status}
                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Client PO</Label>
                  <p className="font-medium">{selectedDN.client_po_number}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Client</Label>
                  <p className="font-medium">{selectedDN.client_name}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Company</Label>
                  <p className="font-medium">{selectedDN.company_name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Type</Label>
                  <p className="font-medium capitalize">{selectedDN.dn_type}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Delivery Date</Label>
                  <p className="font-medium">{selectedDN.delivery_date}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Site Address</Label>
                  <p className="font-medium">{selectedDN.delivery_location || '-'}</p>
                </div>
              </div>

              {/* Ordering Client vs Site split */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="rounded-md border bg-slate-50/50 p-3">
                  <p className="text-xs font-semibold text-slate-700 mb-1">Ordering Client (HO / Billing)</p>
                  <p className="text-sm">
                    <span className="text-muted-foreground">Contact:</span>{' '}
                    {selectedDN.ordering_client_contact_name || selectedDN.contact_person || '-'}
                  </p>
                  <p className="text-sm">
                    <span className="text-muted-foreground">Mobile:</span>{' '}
                    {selectedDN.ordering_client_contact_phone || selectedDN.contact_phone || '-'}
                  </p>
                </div>
                <div className="rounded-md border bg-emerald-50/40 border-emerald-200 p-3">
                  <p className="text-xs font-semibold text-emerald-800 mb-1">Site Delivery Contact</p>
                  <p className="text-sm">
                    <span className="text-muted-foreground">Supervisor:</span>{' '}
                    {selectedDN.site_supervisor_name || '-'}
                  </p>
                  <p className="text-sm">
                    <span className="text-muted-foreground">Mobile:</span>{' '}
                    {selectedDN.site_supervisor_mobile || '-'}
                  </p>
                  {(selectedDN.site_maps_url || selectedDN.site_gps_manual) && (
                    <a
                      href={selectedDN.site_maps_url ||
                        (selectedDN.site_gps_manual &&
                          `https://maps.google.com/?q=${selectedDN.site_gps_manual.latitude},${selectedDN.site_gps_manual.longitude}`)}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-emerald-700 underline mt-1 inline-block"
                      data-testid="site-maps-link"
                    >
                      Open site on Google Maps →
                    </a>
                  )}
                </div>
              </div>

              {/* GPS pin captured with POD photos */}
              {selectedDN.delivery_gps && selectedDN.delivery_gps.latitude != null && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid="dn-delivery-gps">
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-emerald-700 mt-0.5" />
                    <div className="flex-1 text-sm">
                      <p className="font-semibold text-emerald-800">GPS pin captured on site</p>
                      <p className="text-emerald-900 mt-0.5">
                        {Number(selectedDN.delivery_gps.latitude).toFixed(5)},{' '}
                        {Number(selectedDN.delivery_gps.longitude).toFixed(5)}
                        {selectedDN.delivery_gps.accuracy_m != null &&
                          ` (±${Math.round(selectedDN.delivery_gps.accuracy_m)} m)`}
                      </p>
                      <a
                        href={selectedDN.delivery_gps.maps_url ||
                          `https://maps.google.com/?q=${selectedDN.delivery_gps.latitude},${selectedDN.delivery_gps.longitude}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-emerald-700 underline text-xs mt-1 inline-block"
                        data-testid="dn-gps-maps-link"
                      >
                        Open in Google Maps →
                      </a>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-muted-foreground">Subtotal</Label>
                  <p className="font-medium">{selectedDN.currency} {selectedDN.subtotal?.toLocaleString()}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">VAT</Label>
                  <p className="font-medium">{selectedDN.currency} {selectedDN.vat_amount?.toLocaleString()}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Total</Label>
                  <p className="font-bold">{selectedDN.currency} {selectedDN.total?.toLocaleString()}</p>
                </div>
              </div>

              {selectedDN.items?.length > 0 && (
                <div>
                  <Label className="text-muted-foreground">Items</Label>
                  <div className="mt-2 border rounded">
                    <table className="w-full text-sm">
                      <thead className="bg-muted">
                        <tr>
                          <th className="p-2 text-left">Description</th>
                          <th className="p-2 text-right">Qty</th>
                          <th className="p-2 text-left">Driver / Operator</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedDN.items.map((item, idx) => (
                          <tr key={idx} className="border-t align-top">
                            <td className="p-2">{item.description}</td>
                            <td className="p-2 text-right">{item.quantity} {item.unit}</td>
                            <td className="p-2">
                              {item.include_driver ? (
                                <div className="text-xs space-y-0.5">
                                  <div><span className="font-medium">{item.driver_name || '—'}</span> · {item.driver_mobile || '—'}</div>
                                  <div className="text-muted-foreground">Iqama: {item.driver_iqama_number || '—'}</div>
                                  <div className="text-muted-foreground">License: {item.driver_license_number || '—'}</div>
                                </div>
                              ) : (
                                <span className="text-xs text-muted-foreground italic">No operator</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {selectedDN.acknowledged_by && (
                <div>
                  <Label className="text-muted-foreground">Acknowledged By</Label>
                  <p className="font-medium">{selectedDN.acknowledged_by} at {selectedDN.acknowledged_at}</p>
                </div>
              )}

              {(selectedDN.status === 'delivered' || selectedDN.status === 'acknowledged') && (
                <div className="flex items-center justify-between rounded-md border border-emerald-200 bg-emerald-50 p-3">
                  <div>
                    <p className="text-sm font-semibold text-emerald-900">Signed Delivery PDF</p>
                    <p className="text-xs text-emerald-700">
                      Every item's 4-side photos + the client signature stitched into one archivable file.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => handleDownloadSignedPDF(selectedDN)}
                    data-testid="download-signed-pdf-btn"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Download Signed PDF
                  </Button>
                </div>
              )}

              {/* Vehicle Inspection Checklist (Mobilization + Demobilization) */}
              <div className="pt-4 border-t">
                <VehicleInspectionChecklist
                  dnId={selectedDN.id}
                  assetId={selectedDN.items?.find((i) => i.asset_id)?.asset_id}
                />
              </div>

              {/* Per-item Inspection Checklists (mandatory before Finalize) */}
              {selectedDN.status !== 'delivered' && selectedDN.status !== 'acknowledged' && (
                <div className="pt-4 border-t space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold">Per-Item Inspection Checklists</Label>
                    <Badge variant="outline" className={Object.values(inspComplete).every(Boolean) && (selectedDN.items || []).filter(i => i.asset_id).length > 0 ? 'bg-emerald-50 text-emerald-800 border-emerald-300' : 'bg-amber-50 text-amber-800 border-amber-300'}>
                      {(selectedDN.items || []).filter(i => i.asset_id && inspComplete[i.item_id]).length}
                      /{(selectedDN.items || []).filter(i => i.asset_id).length} complete
                    </Badge>
                  </div>
                  {(selectedDN.items || [])
                    .filter((it) => it.asset_id)
                    .map((it, idx) => (
                      <ItemInspectionSection
                        key={it.item_id || idx}
                        dnId={selectedDN.id}
                        item={it}
                        index={idx}
                        onInspectionChange={(next) => handleInspectionChange(it.item_id, next)}
                        onValidityChange={(ok) => handleInspValidityChange(it.item_id, ok)}
                      />
                    ))}

                  {(() => {
                    const inspecItems = (selectedDN.items || []).filter(i => i.asset_id);
                    const allDone = inspecItems.length > 0 && inspecItems.every(i => inspComplete[i.item_id]);
                    return (
                      <div className="pt-2 flex items-center justify-between gap-2 flex-wrap">
                        <p className="text-xs text-muted-foreground">
                          Finalize is disabled until every item's checklist is 100% complete.
                        </p>
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          disabled={!allDone}
                          onClick={() => {
                            if (!allDone) {
                              setConfirmDeliverOpen(true);
                              return;
                            }
                            setConfirmDeliverOpen(true);
                          }}
                          data-testid="finalize-delivery-btn"
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Finalize Delivery
                        </Button>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* POD Photos Section */}
              <div className="pt-4 border-t">
                <div className="flex items-center gap-2 mb-3">
                  <Camera className="h-5 w-5 text-slate-500" />
                  <Label className="text-base font-semibold">Proof of Delivery Photos</Label>
                </div>
                <PODPhotoManager
                  dnId={selectedDN.id}
                  dnNumber={selectedDN.dn_number}
                  initialPhotos={selectedDN.pod_photos || []}
                  readOnly={selectedDN.status === 'acknowledged'}
                  compact={true}
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Finalize Delivery confirmation / incomplete warning */}
      <Dialog open={confirmDeliverOpen} onOpenChange={setConfirmDeliverOpen}>
        <DialogContent data-testid="finalize-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Confirm Delivery Finalization</DialogTitle>
          </DialogHeader>
          {(() => {
            if (!selectedDN) return null;
            const inspecItems = (selectedDN.items || []).filter(i => i.asset_id);
            const incompleteCount = inspecItems.filter(i => !inspComplete[i.item_id]).length;
            if (incompleteCount > 0) {
              return (
                <div className="space-y-3">
                  <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900" data-testid="finalize-incomplete-warning">
                    <p className="font-semibold flex items-center gap-1">
                      <AlertCircle className="h-4 w-4" /> {incompleteCount} item(s) still need their inspection completed
                    </p>
                    <p className="text-xs mt-1">
                      All fields and required photos are mandatory for compliance. Please complete the inspection for every item before finalizing.
                    </p>
                  </div>
                  <div className="flex justify-end">
                    <Button variant="outline" onClick={() => setConfirmDeliverOpen(false)}>Back to inspection</Button>
                  </div>
                </div>
              );
            }
            return (
              <div className="space-y-3">
                <p className="text-sm">
                  All {inspecItems.length} item inspection(s) are complete. Finalize this delivery?
                </p>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setConfirmDeliverOpen(false)}>Cancel</Button>
                  <Button
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="finalize-confirm-btn"
                    onClick={async () => {
                      await handleMarkDelivered(selectedDN.id);
                      setConfirmDeliverOpen(false);
                      setSelectedDN(null);
                    }}
                  >
                    Yes, mark as delivered
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DeliveryNotesV2Page;
